import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, CheckCircle, AlertCircle, Camera, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ sessionId, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('images'); // 'images' or 'video'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (uploadMode === 'video') {
      // Handle video files
      const videoFiles = acceptedFiles.filter(file => 
        file.type.startsWith('video/')
      );
      
      if (videoFiles.length > 0) {
        const video = videoFiles[0];
        setVideoFile({
          file: video,
          id: Math.random().toString(36).substr(2, 9),
          preview: URL.createObjectURL(video),
          name: video.name,
          size: video.size
        });
      }
    } else if (uploadMode === 'quick-analysis') {
      // Handle single image for quick analysis - take only the first image
      const imageFiles = acceptedFiles.filter(file => 
        file.type.startsWith('image/')
      );
      
      if (imageFiles.length > 0) {
        const singleImage = imageFiles[0]; // Only take the first image
        setFiles([{
          file: singleImage,
          id: Math.random().toString(36).substr(2, 9),
          preview: URL.createObjectURL(singleImage),
          status: 'pending'
        }]);
      }
    } else {
      // Handle image files (multiple for regular mode)
      const imageFiles = acceptedFiles.filter(file => 
        file.type.startsWith('image/')
      ).map(file => ({
        file,
        id: Math.random().toString(36).substr(2, 9),
        preview: URL.createObjectURL(file),
        status: 'pending'
      }));
      
      setFiles(prev => [...prev, ...imageFiles]);
    }
  }, [uploadMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: uploadMode === 'video' ? {
      'video/*': ['.mp4', '.avi', '.mov', '.mkv']
    } : {
      'image/*': ['.jpeg', '.jpg', '.png', '.tiff', '.bmp']
    },
    multiple: uploadMode === 'images' // Only allow multiple for regular images mode
  });

  const removeFile = (id) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== id);
      return updated;
    });
  };

  const uploadContent = async () => {
    if (uploadMode === 'images' && files.length === 0) return;
    if (uploadMode === 'video' && !videoFile) return;
    if (uploadMode === 'quick-analysis' && files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      
      if (uploadMode === 'video') {
        formData.append('video', videoFile.file);
        formData.append('images', new File([], 'dummy')); // Backend expects images parameter
      } else {
        // Handle both 'images' and 'quick-analysis' modes
        files.forEach(({ file }) => {
          formData.append('images', file);
        });
        
        // Add a flag for quick analysis mode to skip validation
        if (uploadMode === 'quick-analysis') {
          formData.append('skip_validation', 'true');
        }
      }
      
      const response = await axios.post(
        `${API}/sessions/${sessionId}/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      setValidationResults(response.data);
      setUploadComplete(true);
      
      // Get detailed validation results
      const validationResponse = await axios.get(
        `${API}/sessions/${sessionId}/validation`
      );
      
      if (onUploadComplete) {
        onUploadComplete(validationResponse.data);
      }
      
      // Auto-trigger processing if ready
      if (validationResponse.data.ready_for_processing) {
        setTimeout(async () => {
          try {
            await axios.post(
              `${API}/sessions/${sessionId}/process`,
              {
                ruler_length_cm: 15, // Default ruler length
                enable_depth_analysis: true,
                enable_3d_reconstruction: true
              }
            );
          } catch (error) {
            console.error('Failed to auto-start processing:', error);
          }
        }, 1000); // Small delay to allow UI transition
      }
      
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getUploadStatus = () => {
    if (uploadComplete && validationResults) {
      const validCount = validationResults.validation_summary?.filter(
        result => result.status === 'valid'
      ).length || 0;
      
      // Different messages based on upload mode
      if (uploadMode === 'quick-analysis') {
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          message: `✅ Single image uploaded successfully! Ready for Quick AI Analysis.`,
          color: 'text-green-700'
        };
      } else if (validCount >= 6) {
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          message: `Successfully uploaded ${validCount} valid images. Ready for 3D reconstruction!`,
          color: 'text-green-700'
        };
      } else {
        return {
          icon: <AlertCircle className="w-5 h-5 text-yellow-500" />,
          message: `Uploaded ${validCount} valid images. Need at least 6 for 3D reconstruction.`,
          color: 'text-yellow-700'
        };
      }
    }
    return null;
  };

  const status = getUploadStatus();

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="w-6 h-6" />
          Multi-Angle Burn Image Capture
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Mode Selection */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
          <h4 className="font-semibold text-gray-900 mb-3">📁 Upload Method</h4>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="uploadMode"
                value="images"
                checked={uploadMode === 'images'}
                onChange={(e) => {
                  setUploadMode(e.target.value);
                  setFiles([]);
                  setVideoFile(null);
                  setValidationResults(null);
                  setUploadComplete(false);
                }}
                className="text-blue-600"
              />
              <span className="font-medium">📸 Individual Images</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="uploadMode"
                value="video"
                checked={uploadMode === 'video'}
                onChange={(e) => {
                  setUploadMode(e.target.value);
                  setFiles([]);
                  setVideoFile(null);
                  setValidationResults(null);
                  setUploadComplete(false);
                }}
                className="text-blue-600"
              />
              <span className="font-medium">🎥 MP4 Video</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="uploadMode"
                value="quick-analysis"
                checked={uploadMode === 'quick-analysis'}
                onChange={(e) => {
                  setUploadMode(e.target.value);
                  setFiles([]);
                  setVideoFile(null);
                  setValidationResults(null);
                  setUploadComplete(false);
                }}
                className="text-blue-600"
              />
              <span className="font-medium flex items-center gap-1">
                <Zap className="w-4 h-4 text-yellow-500" />
                Single Image - Quick Analysis
              </span>
            </label>
          </div>
        </div>

        {/* Guidelines */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">
            {uploadMode === 'video' ? '🎥 Video Recording Guidelines' : 
             uploadMode === 'quick-analysis' ? '⚡ Quick Analysis Guidelines' : '📸 Image Capture Guidelines'}
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            {uploadMode === 'video' ? (
              <>
                <li>• Record 15-30 seconds covering all burn angles</li>
                <li>• Move camera slowly in circular motion around burn</li>
                <li>• Include ruler in multiple frames for scale calibration</li>
                <li>• Use consistent lighting throughout recording</li>
                <li>• Maintain 20-30cm distance from wound</li>
                <li>• System will extract 15 frames automatically</li>
                <li>• Supported formats: MP4, AVI, MOV</li>
              </>
            ) : uploadMode === 'quick-analysis' ? (
              <>
                <li>• Upload just 1 clear photo of the burn area</li>
                <li>• Any quality/resolution acceptable - no validation required</li>
                <li>• Advanced AI will analyze directly from your single image</li>
                <li>• Perfect for quick assessments and preliminary analysis</li>
                <li>• Skips multi-angle requirements for immediate results</li>
                <li>• Supported formats: JPG, PNG, TIFF, BMP</li>
              </>
            ) : (
              <>
                <li>• Take 6-12 photos from different angles (360° coverage)</li>
                <li>• Include a ruler in at least 3 photos for scale calibration</li>
                <li>• Use consistent lighting (diffuse daylight preferred)</li>
                <li>• Maintain 20-30cm distance from wound</li>
                <li>• Ensure wound is clean and visible</li>
                <li>• Minimum resolution: 800x600 pixels</li>
              </>
            )}
          </ul>
        </div>

        {/* Drop Zone */}
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${
              isDragActive 
                ? 'border-blue-400 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }
          `}
        >
          <input {...getInputProps()} data-testid="image-upload-input" />
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            {isDragActive 
              ? `Drop ${uploadMode === 'video' ? 'video' : uploadMode === 'quick-analysis' ? 'image' : 'images'} here` 
              : `Drag & drop ${uploadMode === 'video' ? 'MP4 video' : uploadMode === 'quick-analysis' ? 'single image' : 'images'} here`
            }
          </p>
          <p className="text-sm text-gray-500 mb-4">
            or click to select {uploadMode === 'video' ? 'video file' : uploadMode === 'quick-analysis' ? 'single image' : 'files'}
          </p>
          <Button variant="outline" type="button">
            Select {uploadMode === 'video' ? 'Video' : uploadMode === 'quick-analysis' ? 'Image' : 'Images'}
          </Button>
        </div>

        {/* Video Preview */}
        {videoFile && uploadMode === 'video' && (
          <div>
            <h4 className="font-medium mb-3">Selected Video</h4>
            <div className="bg-gray-100 p-4 rounded-lg border">
              <div className="flex items-center gap-4">
                <video
                  src={videoFile.preview}
                  className="w-48 h-32 object-cover rounded border"
                  controls
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{videoFile.name}</div>
                  <div className="text-sm text-gray-500">Size: {(videoFile.size / 1024 / 1024).toFixed(2)} MB</div>
                  <div className="text-xs text-gray-400 mt-2">
                    System will extract ~15 frames automatically for 3D reconstruction
                  </div>
                </div>
                <button
                  onClick={() => setVideoFile(null)}
                  className="bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                  disabled={uploading}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview */}
        {files.length > 0 && (uploadMode === 'images' || uploadMode === 'quick-analysis') && (
          <div>
            <h4 className="font-medium mb-3">
              {uploadMode === 'quick-analysis' ? `Selected Image for Quick Analysis` : `Selected Images (${files.length})`}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {files.map(({ id, preview, file }) => (
                <div key={id} className="relative group">
                  <img
                    src={preview}
                    alt={file.name}
                    className="w-full h-24 object-cover rounded border"
                  />
                  <button
                    onClick={() => removeFile(id)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={uploading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="text-xs text-gray-500 mt-1 truncate">
                    {file.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Uploading images...</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="w-full" />
          </div>
        )}

        {/* Upload Status */}
        {status && (
          <div className={`flex items-center gap-2 p-3 bg-gray-50 rounded-lg ${status.color}`}>
            {status.icon}
            <span className="text-sm font-medium">{status.message}</span>
          </div>
        )}

        {/* Validation Results */}
        {validationResults && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Validation Results</h4>
            <div className="space-y-2 text-sm">
              {validationResults.validation_summary?.map((result, index) => (
                <div key={index} className="flex items-center gap-2">
                  {result.status === 'valid' ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="font-medium">{result.filename}</span>
                  <span className="text-gray-500">-</span>
                  <span className={result.status === 'valid' ? 'text-green-600' : 'text-red-600'}>
                    {result.status}
                  </span>
                  {result.notes?.[0] && (
                    <span className="text-gray-500">({result.notes[0]})</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        <Button 
          onClick={uploadContent}
          disabled={
            (uploadMode === 'images' && files.length === 0) || 
            (uploadMode === 'video' && !videoFile) || 
            (uploadMode === 'quick-analysis' && files.length === 0) || 
            uploading || 
            uploadComplete
          }
          className="w-full"
          size="lg"
          data-testid="upload-button"
        >
          {uploading ? (
            uploadMode === 'video' ? 'Processing Video & Extracting Frames...' : 
            uploadMode === 'quick-analysis' ? 'Uploading for Quick Analysis...' : 'Uploading Images...'
          ) : uploadComplete ? 'Upload Complete' : (
            uploadMode === 'video' ? `Process Video & Extract Frames` : 
            uploadMode === 'quick-analysis' ? `Upload Image for Quick Analysis` :
            `Upload ${files.length} Images`
          )}
        </Button>

        {/* Quick Analysis Continue Button */}
        {uploadComplete && uploadMode === 'quick-analysis' && validationResults && (
          <Button 
            onClick={() => {
              // Auto-call onUploadComplete to trigger navigation
              if (onUploadComplete && validationResults) {
                onUploadComplete({
                  ...validationResults,
                  ready_for_processing: true,
                  quick_analysis_mode: true
                });
              }
            }}
            className="w-full mt-3"
            size="lg"
            data-testid="continue-analysis-button"
            variant="default"
          >
            <Zap className="w-4 h-4 mr-2" />
            Continue to Advanced AI Analysis
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageUpload;